import { FormInput, FormSelect } from "../../components";
import { SegmentedToggle } from "./SegmentedToggle";
import {
  GroupBy,
  amsterdamToday,
  isReportDay,
  rangePresets,
} from "./parcelReportData";

export interface IParcelReportFilters {
  carrierPartnerId: string;
  /** "" for all stores. */
  locationId: string;
  /** Inclusive Amsterdam days, YYYY-MM-DD. */
  dateFrom: string;
  dateTo: string;
}

type Option = { value: string; label: string };

const GROUP_BY_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

interface Props {
  filters: IParcelReportFilters;
  onChange: (changes: Partial<IParcelReportFilters>) => void;
  groupBy: GroupBy;
  onGroupByChange: (groupBy: GroupBy) => void;
  carrierOptions: Option[];
  storeOptions: Option[];
  /** A store admin always sees their own store. */
  isStoreFixed: boolean;
}

export const ParcelReportFilters = ({
  filters,
  onChange,
  groupBy,
  onGroupByChange,
  carrierOptions,
  storeOptions,
  isStoreFixed,
}: Props) => {
  const today = amsterdamToday();

  const setDate = (key: "dateFrom" | "dateTo", value: string) => {
    const changes: Partial<IParcelReportFilters> = {};
    changes[key] = value;
    // Moving one end past the other takes the other end along, so the range
    // never runs backwards. Not for a half-typed year (0002...), which would
    // otherwise drag the other end with it.
    const other = key === "dateFrom" ? filters.dateTo : filters.dateFrom;
    const crosses = key === "dateFrom" ? value > other : value < other;
    if (isReportDay(value) && isReportDay(other) && crosses) {
      changes[key === "dateFrom" ? "dateTo" : "dateFrom"] = value;
    }
    onChange(changes);
  };

  return (
    <div className="w-full px-7 pt-7 bg-white rounded-xl border-gray-border drop-shadow-none mb-4">
      <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2 lg:grid-cols-4">
        <FormSelect
          label="Carrier"
          value={filters.carrierPartnerId}
          options={carrierOptions}
          onChange={(e) => onChange({ carrierPartnerId: e.target.value })}
        />
        {/* The backend pins a store admin to their own store whatever is
            sent; the select is disabled so it does not offer a choice that
            has no effect. */}
        <FormSelect
          label="Store"
          value={isStoreFixed ? "" : filters.locationId}
          options={storeOptions}
          disabled={isStoreFixed}
          onChange={(e) => onChange({ locationId: e.target.value })}
        />
        <FormInput
          label="From"
          type="date"
          value={filters.dateFrom}
          max={today}
          onChange={(e) => setDate("dateFrom", e.target.value)}
        />
        <FormInput
          label="To"
          type="date"
          value={filters.dateTo}
          max={today}
          onChange={(e) => setDate("dateTo", e.target.value)}
        />
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          {rangePresets().map((preset) => {
            const isActive =
              preset.dateFrom === filters.dateFrom &&
              preset.dateTo === filters.dateTo;
            return (
              <button
                key={preset.label}
                type="button"
                aria-pressed={isActive}
                onClick={() =>
                  onChange({
                    dateFrom: preset.dateFrom,
                    dateTo: preset.dateTo,
                  })
                }
                className={`h-8 px-3 border rounded font-satoshi font-medium text-xs sm:text-sm whitespace-nowrap ${
                  isActive
                    ? "border-droppie-oranje bg-droppie-oranje/10 text-droppie-oranje"
                    : "border-gray-border bg-white text-gray-text"
                }`}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <span className="font-satoshi text-sm text-gray-text whitespace-nowrap">
            Group by
          </span>
          <SegmentedToggle
            value={groupBy}
            options={GROUP_BY_OPTIONS}
            onChange={onGroupByChange}
          />
        </div>
      </div>

      <p className="font-satoshi text-sm text-gray-text pb-7">
        Parcels registered in the Droppie app. Days are in Amsterdam time.
      </p>
    </div>
  );
};
